import { formatCurrency } from 'common/FormatUtilities';
import React from 'react';
import { TextReplace } from 'src/shared/design-system/Utilities';
import { TierType } from '../ProgressBar/types';
import { BulkDiscountBreakdown } from './BulkDiscountBreakdown/BulkDiscountBreakdown';
import { BREAKDOWN_DISCOUNTED_DELIVERY_TEXT, REWARDS_DISCOUNT_TEXT, TOTAL_SAVINGS_TEXT } from './consts';
import { useBreakdown, useBulkDiscount } from './hooks';
import {
  BreakdownContainer,
  Container,
  DropdownIcon,
  ProgressBar,
  RewardsContainer,
  RewardsText,
  RewardsWrapper,
  TierIcon,
  TotalSavings,
  TotalSavingsContainer,
  TotalSavingsWrapper,
  Wrapper,
} from './styles';
import { BulkDiscountProps } from './types';

const createTier = (percent: number): TierType => ({
  percent,
  renderContent: () => <TierIcon />,
});

export const BulkDiscount: React.FC<BulkDiscountProps> = (props) => {
  const {
    amountForDiscount,
    breakdownProps,
    discountedDeliveryFee,
    isMaxRewardsReached,
    isExpanded,
    isDiscountedDeliveryReached,
    handleExpand,
    handleCollapse,
    hasItemsInCart,
    percent,
    rewardsText,
    renderContentWithStyle,
    totalSavings,
    tiers,
    ...progressProps
  } = useBulkDiscount(props);
  const { isBreakdownOpen, toggleBreakdown } = useBreakdown({ isExpanded, handleExpand, handleCollapse });
  const formattedTiers = tiers.map((tier) => createTier(tier.percent));

  return (
    <Container>
      <Wrapper>
        <ProgressBar percent={percent} {...progressProps} tiers={formattedTiers} />
        <TotalSavingsContainer>
          {!!totalSavings && hasItemsInCart && (
            <TotalSavingsWrapper>
              <TotalSavings
                replaceText={<span key='total-savings-text'>{formatCurrency(totalSavings)}</span>}
                text={TOTAL_SAVINGS_TEXT}
              />
            </TotalSavingsWrapper>
          )}
          {isDiscountedDeliveryReached && !totalSavings && (
            <TotalSavingsWrapper>
              <TextReplace
                replaceText={[discountedDeliveryFee === 0 ? 'Free' : formatCurrency(discountedDeliveryFee)]}
                text={BREAKDOWN_DISCOUNTED_DELIVERY_TEXT}
              />
            </TotalSavingsWrapper>
          )}
        </TotalSavingsContainer>
        <RewardsContainer>
          <RewardsWrapper onClick={toggleBreakdown}>
            {isMaxRewardsReached ? (
              <RewardsText>{rewardsText}</RewardsText>
            ) : (
              <>
                <TextReplace
                  replaceText={[
                    <span className='font-bold' key='amount-for-discount'>
                      {formatCurrency(amountForDiscount)}
                    </span>,
                    <RewardsText key={`rewards-text-${rewardsText}`} isBolded>
                      {rewardsText}
                    </RewardsText>,
                  ]}
                  text={REWARDS_DISCOUNT_TEXT}
                />
              </>
            )}
            <DropdownIcon isOpen={isBreakdownOpen} />
          </RewardsWrapper>
        </RewardsContainer>
      </Wrapper>
      <BreakdownContainer show={isBreakdownOpen} renderContentWithStyle={renderContentWithStyle}>
        <BulkDiscountBreakdown {...breakdownProps} />
      </BreakdownContainer>
    </Container>
  );
};
